import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Search, Pencil } from 'lucide-react';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { useLocale, readStoredLocale } from '../context/LocaleContext';
import { normalizeDashboardLocale } from '../i18n/messages';
import { dashboardApi } from '../api/dashboardApi';
import BotCursor from './BotCursor';
import UserCursorTracker from './UserCursorTracker';
import WorkspaceSwitcher from './shell/WorkspaceSwitcher';
import UserProfileCard from './shell/UserProfileCard';
import SidebarNav from './shell/SidebarNav';
import {
  DASHBOARD_BOT_NAVIGATE_PATH,
  DASHBOARD_BOT_SECTION_PATH,
  sectionSlugForDashboardPath,
} from '../nav/botNavRoutes';
import {
  getBotDemoSessionId,
  persistBotDemoSessionId,
  reportDashboardPageLoaded,
} from '../utils/botSessionBridge';
import '../styles/custio-theme.css';
import '../pages/Dashboard.css';

const DashboardDataContext = createContext(null);

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    throw new Error('useDashboardData must be used within DashboardShell');
  }
  return ctx;
}

const BOT_ONLY_NAV = (process.env.REACT_APP_DASHBOARD_BOT_ONLY_NAV || '').trim() === '1';

function BotNavigationListener() {
  const navigate = useNavigate();
  const socketUrl = (process.env.REACT_APP_MAIN_BACKEND_URL || '').trim();

  useEffect(() => {
    if (!socketUrl) return undefined;

    const socket = io(socketUrl);
    const onAction = (data) => {
      if (!data?.action || typeof data.action !== 'string') return;
      if (!data.action.startsWith('navigate-')) return;
      const section = data.action.replace('navigate-', '');
      window.dispatchEvent(new CustomEvent('bot-navigate', { detail: { section } }));
      requestAnimationFrame(() => {
        const path = DASHBOARD_BOT_NAVIGATE_PATH[data.action];
        if (path) navigate(path);
      });
    };
    socket.on('bot-action', onAction);
    return () => socket.disconnect();
  }, [navigate, socketUrl]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'demo-session' && event.data.sessionId) {
        persistBotDemoSessionId(event.data.sessionId);
        return;
      }
      if (event.data?.type === 'navigate') {
        const path = DASHBOARD_BOT_SECTION_PATH[event.data.section];
        const section = event.data.section;
        if (path && section) {
          window.dispatchEvent(new CustomEvent('bot-navigate', { detail: { section } }));
          requestAnimationFrame(() => navigate(path));
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate]);

  return null;
}

export default function DashboardShell() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, setLocale } = useLocale();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const payload = await dashboardApi('/api/dashboard/data');
      setData(payload);
    } catch (e) {
      setError(e.message || 'Could not load dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /** Pick up ?demoSessionId= / ?sessionId= so the bot can correlate iframe ↔ session. */
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const q = (sp.get('demoSessionId') || sp.get('sessionId') || '').trim();
    if (q) persistBotDemoSessionId(q);
  }, [location.search]);

  /** After each route, tell the bot the page slug (dashboardPageCopy.js keys) for page-loaded handshake. */
  useEffect(() => {
    if (!data) return;
    const slug = sectionSlugForDashboardPath(location.pathname);
    if (slug && getBotDemoSessionId()) {
      reportDashboardPageLoaded(slug);
    }
  }, [data, location.pathname]);

  useEffect(() => {
    if (!data?.settings) return;
    if (readStoredLocale()) return;
    const n = normalizeDashboardLocale(data.settings.siteLanguage);
    if (n) setLocale(n);
  }, [data?.settings, data, setLocale]);

  const persistLanguageToServer = useCallback(
    async (next) => {
      const s = data?.settings;
      if (!s) return;
      await dashboardApi('/api/dashboard/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          botName: s.botName || 'AI Demo Assistant',
          greeting: s.greeting || '',
          responseSpeed: s.responseSpeed || 'normal',
          voiceEnabled: s.voiceEnabled !== false,
          screenShareEnabled: s.screenShareEnabled !== false,
          accountName: s.accountName ?? '',
          siteLanguage: next,
          incomingEmailDomain: s.incomingEmailDomain ?? '',
          supportEmail: s.supportEmail ?? '',
        }),
      });
      await reload();
    },
    [data?.settings, reload]
  );

  const profile = data?.profile;
  const displayName = profile?.displayName || user?.email?.split('@')[0] || 'User';
  const email = profile?.email || user?.email || '';
  const initial = (displayName || 'U').charAt(0).toUpperCase();
  const workspaceName =
    (process.env.REACT_APP_WORKSPACE_NAME || '').trim() ||
    data?.settings?.accountName ||
    displayName ||
    t('common.workspaceFallback');

  const ctxValue = useMemo(
    () => ({ data, loading, error, reload }),
    [data, loading, error, reload]
  );

  return (
    <DashboardDataContext.Provider value={ctxValue}>
      <div className="custio-shell">
        {BOT_ONLY_NAV && (
          <div className="bot-control-badge" role="status">
            {t('shell.botControlBadge')}
          </div>
        )}

        <aside className="custio-sidebar" aria-label={t('shell.mainNavAria')}>
          <WorkspaceSwitcher
            workspaceName={workspaceName}
            onLanguageChange={data?.settings ? persistLanguageToServer : undefined}
          />
          <div className="custio-search-row">
            <label className="custio-search">
              <Search size={16} strokeWidth={2} color="#6b7280" aria-hidden />
              <input type="search" placeholder={t('shell.searchPlaceholder')} autoComplete="off" />
            </label>
            <button type="button" className="custio-compose-btn" aria-label={t('shell.composeAria')}>
              <Pencil size={18} strokeWidth={2} />
            </button>
          </div>

          <nav className="custio-sidebar__scroll" aria-label={t('shell.workspaceNavAria')}>
            <SidebarNav />
          </nav>

          <UserProfileCard displayName={displayName} email={email} initial={initial} onLogout={logout} />
        </aside>

        {loading && !data && (
          <div className="custio-main">
            <div className="custio-panel">
              <div className="dashboard-page-state">
                <div className="dashboard-page-state__spinner" />
                <p style={{ color: '#6b7280' }}>{t('shell.loadingWorkspace')}</p>
              </div>
            </div>
          </div>
        )}

        {error && !data && (
          <div className="custio-main">
            <div className="custio-panel">
              <div className="dashboard-page-state dashboard-page-state--error">
                <p>{error}</p>
                <button type="button" className="custio-btn-primary" onClick={reload}>
                  {t('shell.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        )}

        {data && (
          <div className="custio-app-main">
            <Outlet key={location.pathname} />
          </div>
        )}

        <BotNavigationListener />
        <BotCursor />
        <UserCursorTracker />
      </div>
    </DashboardDataContext.Provider>
  );
}
